/**
 * Feed vocabulary (#563) — the SINGLE source of every NEW operator-facing string
 * the two-tab News surface adds. Centralized so the `.lock` scan
 * (feedVocabulary.lock.test.ts) can reflectively enforce that none of it drifts
 * into a forbidden vocabulary group (alert-issuance / US-ICS / own-family /
 * chat-DM), the same guard the Ontario-IMS + default-view locks use.
 *
 * The SHIPPED honesty invariants ("trending · not confirmed", "✓ official",
 * "AI-generated summary · N sources", "not yet confirmed", "ESB read-only") are
 * re-declared here too so the lock proves they stay verbatim and forbidden-clean.
 *
 * NOTE on "Raw chatter": the chat-DM forbidden guard matches `\bchat\b` (word
 * boundary) — "chatter" is a DISTINCT word and is NOT matched (verified in the
 * lock test), so the founder-approved section label is safe.
 */

// ── Tab bar ──────────────────────────────────────────────────────────────────
// The founder RULED these two labels verbatim in the binding executive directive
// (docs/founder-directives-2026-07-06.md §2 — founder Christopher, 2026-07-06):
// "VOCABULARY (IMS-ruled — verbatim these, no invention)". "Situation Briefs"
// names the EXISTING Emerging Situation Brief feature (§4.44 / #295), a shipped
// operator term distinct from the §4.19 SITREP artifact. [FOUNDER-FLAG] if a §4.3
// doctrine-reference exception record is wanted, it rides the founder's directive.
export const SITUATION_BRIEFS_TAB = 'Situation Briefs';
export const NEWS_WIRE_TAB = 'News & Wire';

/** Briefs tab badge = the LIVE brief count (absent at 0). */
export function briefsBadge(liveCount: number): string {
  return String(liveCount);
}

// ── Cross-tab urgent-official band (#563 placement; #373-verbatim copy) ──────
export const OFFICIAL_ALERTS_BAND_HEADER = 'Official alerts';
export const OFFICIAL_ALERTS_BAND_SUBLABEL = 'verified authority · elevated for review';

// ── Section headers / dividers ───────────────────────────────────────────────
export const SOCIAL_MEDIA_SECTION = 'Social media';
export const RAW_CHATTER_LABEL = 'Raw chatter';
export const EARLIER_BRIEFS_DIVIDER = '— Earlier briefs —';
export const SURGE_OVER_SUBLABEL = 'surge over · kept for review';

// ── Empty / quiet states ─────────────────────────────────────────────────────
export const BRIEFS_QUIET_STATE = 'No emerging situations — chatter is quiet.';
export const NEWS_EMPTY_STATE = 'No signals match the current filters.';
export const NEWS_EMPTY_FOCUS_STATE = 'No geo-tagged reports near this incident in the window.';
export const WIRE_LOADING = 'Loading the wire…';
export const WIRE_UNAVAILABLE = 'Wire unavailable — retrying.';

// ── Liveness ─────────────────────────────────────────────────────────────────
/** Per-tab "new since your cursor" reveal pill — streams never auto-insert. */
export function newSincePill(n: number): string {
  return `▲ ${n} new — show`;
}

/** The live/updated freshness stamp, driven by SERVER data freshness. */
export function liveStamp(hhmm: string): string {
  return `● live · updated ${hhmm}`;
}

/** The stale badge — server freshness exceeds the threshold. Operator honesty:
 *  a stale server returning 200s trips THIS, not a false "live". */
export function staleBadge(minutesOld: number): string {
  return `STALE · newest data ${minutesOld} min old`;
}

/** The "as of" stamp shown on the quiet Briefs tab so an empty tab still proves
 *  the wire is live. */
export function wireLiveAsOf(hhmm: string): string {
  return `Wire live · as of ${hhmm}`;
}

// ── StoryCard diversity chip ─────────────────────────────────────────────────
/**
 * "N outlets · <lead> ×copies" — DISTINCT outlets (corroboration breadth) shown
 * separately from raw copy count. Repeats are NOT corroboration.
 */
export function diversityChip(distinctOutlets: number, leadOutlet: string, copies: number): string {
  const outlets = `${distinctOutlets} ${distinctOutlets === 1 ? 'outlet' : 'outlets'}`;
  return `${outlets} · ${leadOutlet} ×${copies}`;
}

// ── Shipped honesty invariants (kept verbatim; the lock proves they stay clean) ─
export const TRENDING_NOT_CONFIRMED = 'trending · not confirmed';
export const OFFICIAL_BADGE = '✓ official';
export const OFFICIALS_REPORTING = '✓ officials reporting';
export const AI_SUMMARY_PREFIX = 'AI-generated summary';
export const NOT_YET_CONFIRMED = 'not yet confirmed';
export const ESB_READ_ONLY = 'ESB read-only';
export const MEDIA_CHIP = '📸 media';
